import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { validateRegistrationToken, TokenValidationResponse } from '../services/registrationApi';
import RegisterForm from '../components/register/RegisterForm';
import { Lock, X, Shield, Loader2, CheckCircle } from 'lucide-react';

// --- Sub-views ---

function TokenValidating() {
  return (
    <div className="bg-white/80 backdrop-blur-lg shadow-xl rounded-2xl px-8 py-12 border border-gray-100">
      <div className="flex flex-col items-center justify-center space-y-6">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-gray-200 rounded-full animate-pulse" />
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-niwt-primary rounded-full animate-spin border-t-transparent" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-niwt-text mb-2">
            Validating Your Invitation
          </h3>
          <p className="text-sm text-gray-600">
            Please wait while we verify your registration link...
          </p>
        </div>
        <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
          <Lock className="w-4 h-4" />
          <span>Secure connection verified</span>
        </div>
      </div>
    </div>
  );
}

function InvalidTokenView({ error = 'Invalid or expired token' }: { error?: string }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white/80 backdrop-blur-lg shadow-xl rounded-2xl px-8 py-12 border border-gray-100">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-10 h-10 text-red-600" />
          </div>
        </div>

        <h3 className="text-2xl font-bold text-center text-niwt-text mb-4">
          Invalid or Expired Invitation
        </h3>

        <p className="text-gray-600 text-center mb-8">
          This invitation link has expired or has already been used. For security reasons,
          invitation links expire after 72 hours.
        </p>

        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h4 className="font-semibold text-niwt-text mb-3">What to do next:</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-niwt-primary mr-2">•</span>
              <span>Contact your administrator for a new invitation</span>
            </li>
            <li className="flex items-start">
              <span className="text-niwt-primary mr-2">•</span>
              <span>If you believe this is an error, email support@niwt.com</span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => navigate('/login')}
            className="flex-1 bg-niwt-primary hover:bg-niwt-secondary text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Return to Login
          </button>
          <a
            href="https://www.niwt.com/waitlist"
            className="flex-1 bg-white hover:bg-gray-50 text-niwt-primary border-2 border-niwt-primary font-semibold py-3 px-6 rounded-lg transition-colors duration-200 text-center"
          >
            Join Waiting List
          </a>
        </div>

        <div className="border-t pt-6">
          <div className="flex items-center justify-center text-xs text-gray-500 bg-red-50 px-4 py-3 rounded-lg">
            <Lock className="w-4 h-4 mr-2 text-red-600" />
            <span>This attempt has been logged for security purposes.</span>
          </div>
        </div>

        {error && error !== 'Invalid or expired token' && (
          <div className="mt-4 text-center text-xs text-gray-500">
            Error details: {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessView({ firstName }: { firstName: string }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setRedirecting(true);
          navigate('/chat');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoToChat = () => {
    setRedirecting(true);
    navigate('/chat');
  };

  return (
    <div className="bg-gradient-to-br from-niwt-primary/5 to-niwt-secondary/5 rounded-2xl p-1">
      <div className="bg-white/90 backdrop-blur-lg shadow-xl rounded-2xl px-8 py-12 border border-gray-100">
        <div className="max-w-md mx-auto">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-center text-niwt-text mb-4">
            Account Created Successfully!
          </h3>

          <p className="text-gray-600 text-center mb-8">
            Welcome to Niwt, {firstName}! You're now being redirected to your chat assistant.
          </p>

          <div className="flex justify-center mb-8">
            <button
              onClick={handleGoToChat}
              className="bg-niwt-primary hover:bg-niwt-secondary text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Go to Chat Now
            </button>
          </div>

          <div className="text-center text-sm text-gray-500">
            {redirecting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirecting to chat...
              </span>
            ) : (
              <span>Redirecting to chat in {countdown} seconds...</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Register Page ---

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validationState, setValidationState] = useState<'validating' | 'valid' | 'invalid' | 'success'>('validating');
  const [tokenData, setTokenData] = useState<TokenValidationResponse | null>(null);
  const [userFirstName, setUserFirstName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invitation token provided');
        setValidationState('invalid');
        return;
      }

      if (token.length < 32) {
        setError('Invalid token format');
        setValidationState('invalid');
        return;
      }

      try {
        const response = await validateRegistrationToken(token);
        setTokenData(response);

        if (response.valid) {
          setValidationState('valid');
        } else {
          setError(response.error || 'Invalid or expired token');
          setValidationState('invalid');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Failed to validate token. Please try again.');
        setValidationState('invalid');
      }
    };

    validateToken();
  }, [token]);

  const handleRegistrationSuccess = (firstName: string) => {
    setUserFirstName(firstName);
    setValidationState('success');
  };

  const renderContent = () => {
    switch (validationState) {
      case 'validating':
        return <TokenValidating />;
      case 'invalid':
        return <InvalidTokenView error={error} />;
      case 'valid':
        if (!tokenData?.email || !tokenData?.company || !token) {
          return <InvalidTokenView error="Invalid token data" />;
        }
        return (
          <RegisterForm
            email={tokenData.email}
            company={tokenData.company}
            firstName={tokenData.firstName}
            lastName={tokenData.lastName}
            title={tokenData.title}
            phone={tokenData.phone}
            token={token}
            onSuccess={handleRegistrationSuccess}
          />
        );
      case 'success':
        return <SuccessView firstName={userFirstName} />;
      default:
        return <InvalidTokenView error="Unknown error occurred" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-niwt-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-niwt-secondary/5 rounded-full blur-3xl" />
      </div>

      {/* Logo and Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <Link to="/login" className="flex justify-center">
          <img
            src="/images/niwt-logo.png"
            alt="Niwt"
            width={60}
            height={60}
            className="hover:scale-105 transition-transform"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-libre font-bold text-niwt-text">
          Create Your Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Complete your registration to access Niwt
        </p>
      </div>

      {/* Main Content */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-[600px] z-10">
        {renderContent()}
      </div>

      {/* Footer Links */}
      <div className="mt-8 text-center z-10">
        <div className="text-sm text-gray-600">
          <Link to="/login" className="text-niwt-primary hover:text-niwt-secondary transition-colors">
            Already have an account? Sign in
          </Link>
          <span className="mx-2">•</span>
          <Link to="/help" className="text-niwt-primary hover:text-niwt-secondary transition-colors">
            Need help?
          </Link>
        </div>
        <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
          <Shield className="w-3 h-3 mr-1" />
          Secured with multi-factor authentication
        </div>
      </div>
    </div>
  );
};

export default Register;
